import { pantryColors } from '@pantry/ui'
import React from 'react'
import type { CampaignType } from '../../../../types/loyaltyMarketing'

const p = pantryColors

// Reference emerald tokens (breadstack-loyalty-pro.html :root) — pantryColors
// has no emerald ramp, so the values are inlined here like the mock's
// --emerald/--emerald-bg.
export const EMERALD = '#059669'
export const EMERALD_BG = '#ECFDF5'

// Per-type tint (icon chip background + icon/foreground color), mirroring the
// mockup's `.type-card.{email,sms,cart}` / `.camp-icon.{email,sms,cart}`:
// email = blue, sms = teal, abandoned cart = orange.
export const CAMPAIGN_TYPE_TINTS: Record<
  CampaignType,
  { fg: string; bg: string }
> = {
  email: { fg: p.blue[700], bg: p.blue[50] },
  sms: { fg: p.teal[700], bg: p.teal[50] },
  abandoned_cart: { fg: p.orange[700], bg: p.orange[50] },
}

// Inline SVG glyphs reproduced from the mockup's typeIcon(type) map
// (breadstack-campaigns.html): envelope, chat bubble, shopping cart.
const ICON_PATHS: Record<CampaignType, React.ReactNode> = {
  email: (
    <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  ),
  sms: <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />,
  abandoned_cart: (
    <path d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
  ),
}

export const CampaignTypeIcon: React.FC<{
  type: CampaignType
  size?: number
}> = ({ type, size = 20 }) => (
  <svg
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.7}
    viewBox="0 0 24 24"
    aria-hidden
  >
    {ICON_PATHS[type] ?? ICON_PATHS.email}
  </svg>
)

// Always-on flow glyphs (list rows + drawer share these): birthday = cake,
// any other automation = lightning bolt.
export const BIRTHDAY_TINT = { fg: '#DB2777', bg: '#FDF2F8' }

export const FlowIcon: React.FC<{
  kind?: 'birthday' | 'bolt'
  size?: number
}> = ({ kind = 'bolt', size = 20 }) => (
  <svg
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    viewBox="0 0 24 24"
    aria-hidden
  >
    {kind === 'birthday' ? (
      <>
        <path d="M20 21V11a2 2 0 00-2-2H6a2 2 0 00-2 2v10" />
        <path d="M2 21h20M7 9V5.5a2.5 2.5 0 015 0V9" />
      </>
    ) : (
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    )}
  </svg>
)

// Icon + tint for a flow row (list and drawer): abandoned cart = cart/orange,
// sms = chat/teal, birthday = cake/pink, anything else = bolt in the brand tint.
export const flowVisual = (
  listType: 'abandoned_cart' | 'sms' | 'flow',
  triggerMetric: string,
  size = 20,
): { icon: React.ReactNode; tint: { fg: string; bg: string } } => {
  if (listType === 'abandoned_cart' || listType === 'sms')
    return {
      icon: <CampaignTypeIcon type={listType} size={size} />,
      tint: CAMPAIGN_TYPE_TINTS[listType],
    }
  if (triggerMetric === 'Birthday')
    return {
      icon: <FlowIcon kind="birthday" size={size} />,
      tint: BIRTHDAY_TINT,
    }
  return {
    icon: <FlowIcon size={size} />,
    tint: { fg: p.brand.primary, bg: p.brand.light },
  }
}
